import _ from 'lodash'

import i18n from '@/i18n/i18n'
import RetailerService from '@/services/retailer/RetailerService.js'

import { errorMessage } from '@/utilities/helpers.js'

import { RETAILER_ERROR_MESSAGES } from '@/constants/errorMessages'
import { TRADE_TYPE } from '@/constants/tradeType'

const { t } = i18n.global

export const namespaced = true

export const state = {
  retailer: {},
  salesChannels: null,
  categories: [],
  agriInputMarketMobileProducts: [],
  cropMarketMobileProducts: [],
  retailerContacts: []
}

export const mutations = {
  SET_RETAILER(state, retailer) {
    state.retailer = retailer
  },
  SET_SALES_CHANNELS(state, salesChannels) {
    state.salesChannels = salesChannels
  },
  UPDATE_RETAILER(state, retailer) {
    state.retailer = retailer
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories
  },
  SET_AGRI_INPUT_MARKET_MOBILE_PRODUCTS(state, products) {
    state.agriInputMarketMobileProducts = products
  },
  SET_CROP_MARKET_MOBILE_PRODUCTS(state, products) {
    state.cropMarketMobileProducts = products
  },
  SET_RETAILER_CONTACTS(state, contacts) {
    state.retailerContacts = contacts
  },
  ADD_RETAILER_CONTACTS(state, contact) {
    state.retailerContacts.push(contact)
  },
  DELETE_RETAILER_CONTACT(state, contactId) {
    state.retailerContacts = state.retailerContacts.filter(contact => contact.id !== contactId)
  },
  UPDATE_RETAILER_CONTACT(state, contact) {
    const index = state.retailerContacts.findIndex(c => c.id === contact.id)
    state.retailerContacts[index] = contact
  }
}

export const actions = {
  getRetailer({ commit, state }) {
    if (state.retailer.id) {
      return state.retailer
    }

    return new Promise(resolve => {
      RetailerService.getRetailer().then(response => {
        commit('SET_RETAILER', response.data)
        resolve(response.data)
      })
    })
  },
  getSalesChannels({ commit, state, rootState }) {
    if (state.salesChannels) return state.salesChannels

    return new Promise(resolve => {
      RetailerService.getSalesChannels(rootState.retailerAuth.user.retailer_id).then(response => {
        commit('SET_SALES_CHANNELS', response.data)
        resolve(response.data)
      })
    })
  },
  getCategories({ commit }) {
    return new Promise((resolve, reject) => {
      RetailerService.getCategories()
        .then(response => {
          commit('SET_CATEGORIES', response.data)
          resolve(response.data)
        })
        .catch(err => {
          throw err
        })
    })
  },
  editRetailer({ dispatch, commit }, retailer) {
    return new Promise((resolve, reject) => {
      RetailerService.editRetailer(retailer)
        .then(response => {
          commit('UPDATE_RETAILER', { retailer: response.data })
          const notification = {
            type: 'success',
            message: t('store.shared.data_updated')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.editRetailerError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  checkBrands({ dispatch }) {
    return new Promise((resolve, reject) => {
      RetailerService.checkBrands()
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.checkBrandsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getMobileProducts({ dispatch, commit }, tradeType) {
    return new Promise((resolve, reject) => {
      RetailerService.getMobileProducts(tradeType)
        .then(response => {
          let products = []
          _.forEach(response.data, function (category) {
            let categoryName = category.name
            _.forEach(category.sub_categories, function (subCategory) {
              _.remove(subCategory.products, function (product) {
                return product.units.length === 0
              })

              subCategory.products.forEach((product, index) => {
                subCategory.products[index]['category_id'] = category.id
                subCategory.products[index]['group'] = category.group
                if (!product.brands.length) {
                  subCategory.products[index]['brands'] = category.brands
                }
                if (subCategory.products[index].orders_count > 0) {
                  favoriteProducts.push(subCategory.products[index])
                }
              })

              let obj = {
                category: categoryName + ' - ' + subCategory.name,
                products: subCategory.products
              }

              products.push(obj)
            })
          })

          switch (tradeType) {
            default:
            case TRADE_TYPE.AGRI_INPUT_MARKET:
              commit('SET_AGRI_INPUT_MARKET_MOBILE_PRODUCTS', products)
              break
            case TRADE_TYPE.CROP_MARKET:
              commit('SET_CROP_MARKET_MOBILE_PRODUCTS', products)
          }

          resolve(products)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.getMobileProductsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  removeMobileProduct({ dispatch, commit }, retailerMobileProductId) {
    return new Promise((resolve, reject) => {
      RetailerService.removeMobileProduct(retailerMobileProductId)
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.removeMobileProductError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRetailerContacts({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      RetailerService.getRetailerContacts()
        .then(response => {
          commit('SET_RETAILER_CONTACTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.getRetailerContactsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  saveRetailerContact({ dispatch, commit }, payload) {
    const { contact } = payload
    return new Promise((resolve, reject) => {
      RetailerService.saveRetailerContact(contact)
        .then(response => {
          commit('ADD_RETAILER_CONTACTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.saveRetailerContactError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteRetailerContact({ dispatch, commit }, payload) {
    const { contactId } = payload
    return new Promise((resolve, reject) => {
      RetailerService.deleteRetailerContact(contactId)
        .then(response => {
          commit('DELETE_RETAILER_CONTACT', contactId)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.deleteRetailerContactError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateRetailerContact({ dispatch, commit }, payload) {
    const { contact } = payload
    return new Promise((resolve, reject) => {
      RetailerService.updateRetailerContact(contact.id, contact)
        .then(response => {
          commit('UPDATE_RETAILER_CONTACT', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.editRetailerContactError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
